// Product video upload (1023, MEDIA purpose `product_video`): MP4, WebM or MOV up to 50 MB, kept as
// uploaded (no re-encoding in the browser). Before the upload the browser reads the length and
// captures a frame as the poster, which goes up as an ordinary product photo. Then: ticket → PUT the
// file (XHR, so the bar shows real progress; R2 checks the SHA-256) → complete (the server sniffs the
// container's magic bytes).
import { API_BASE, ApiError, reviveMocks } from "../api/client";
import { catalogApi, type MediaAsset } from "../products/api";
import { uploadPhoto, UploadRejected } from "./upload";

export const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const POSTER_EDGE = 1600;

export type VideoPhase = "preparing" | "uploading" | "finishing";
export interface UploadedVideo {
  asset: MediaAsset;
  poster: MediaAsset | null;
  durationMs: number | null;
}

/** The video's type; some systems leave `.mov` (and others) untyped, so the extension decides then. */
export function videoType(file: File): (typeof VIDEO_TYPES)[number] | null {
  const t = file.type.toLowerCase();
  if ((VIDEO_TYPES as readonly string[]).includes(t)) return t as (typeof VIDEO_TYPES)[number];
  if (t && t !== "application/octet-stream") return null;
  const ext = file.name.toLowerCase().split(".").pop();
  return ext === "mp4" || ext === "m4v"
    ? "video/mp4"
    : ext === "webm"
      ? "video/webm"
      : ext === "mov"
        ? "video/quicktime"
        : null;
}

/** Length and a poster frame (≈1 s in), read by the browser. Either may be null (codec unsupported). */
function probe(file: File): Promise<{ durationMs: number | null; poster: Blob | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let settled = false;
    const finish = (out: { durationMs: number | null; poster: Blob | null }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      v.removeAttribute("src");
      v.load();
      resolve(out);
    };
    const timer = setTimeout(() => finish({ durationMs: null, poster: null }), 10_000);
    let durationMs: number | null = null;
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.onerror = () => finish({ durationMs, poster: null });
    v.onloadedmetadata = () => {
      durationMs = Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : null;
      v.currentTime = Math.max(0.1, Math.min(1, (v.duration || 0) / 2));
    };
    v.onseeked = () => {
      const w = v.videoWidth;
      const h = v.videoHeight;
      if (!w || !h) return finish({ durationMs, poster: null });
      const scale = Math.min(1, POSTER_EDGE / Math.max(w, h));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return finish({ durationMs, poster: null });
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => finish({ durationMs, poster: b }), "image/jpeg", 0.86);
    };
    v.src = url;
  });
}

async function sha256Hex(blob: Blob): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** PUT with upload progress (fetch has none); retried once after a dev mock-worker restart. */
async function putWithProgress(
  url: string,
  body: Blob,
  type: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await putOnce(url, body, type, onProgress, signal);
  if (!(await reviveMocks(res))) return;
  const again = await putOnce(url, body, type, onProgress, signal);
  if ((again.headers.get("content-type") ?? "").startsWith("text/html"))
    throw new ApiError(0, "network", undefined, undefined, undefined, undefined);
}

function putOnce(
  url: string,
  body: Blob,
  type: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ headers: { get(name: string): string | null } }> {
  const path = url.startsWith(API_BASE) ? url : `${API_BASE}${url}`;
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("PUT", path);
    x.withCredentials = true;
    x.setRequestHeader("Content-Type", type);
    x.setRequestHeader("X-Ady-Client", "1");
    x.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    x.onload = () => {
      const res = { headers: { get: (name: string) => x.getResponseHeader(name) } };
      // An HTML answer is Vite (dev mock worker restarted): the caller re-activates and retries.
      const html =
        "__ADY_MOCK_REVIVE__" in globalThis &&
        (x.getResponseHeader("content-type") ?? "").startsWith("text/html");
      if ((x.status >= 200 && x.status < 300) || html) return resolve(res);
      let code = "internal";
      let rid: string | undefined;
      try {
        const j = JSON.parse(x.responseText) as { error?: { code?: string; request_id?: string } };
        code = j.error?.code ?? code;
        rid = j.error?.request_id;
      } catch {
        // not JSON
      }
      reject(new ApiError(x.status, code as "internal", rid, undefined, undefined, undefined));
    };
    x.onerror = () =>
      reject(new ApiError(0, "network", undefined, undefined, undefined, undefined));
    x.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    signal?.addEventListener("abort", () => x.abort(), { once: true });
    x.send(body);
  });
}

/** One video file → a ready `product_video` asset (+ its poster photo when the browser can read it). */
export async function uploadVideo(
  orgId: string,
  storeId: string,
  file: File,
  onProgress: (phase: VideoPhase, percent: number) => void,
  signal?: AbortSignal,
): Promise<UploadedVideo> {
  const type = videoType(file);
  if (!type) throw new UploadRejected("type");
  if (file.size > MAX_VIDEO_BYTES) throw new UploadRejected("size");
  onProgress("preparing", 0);
  const meta = await probe(file);
  const base = file.name.replace(/\.[^.]+$/, "").slice(0, 150) || "video";
  let poster: MediaAsset | null = null;
  if (meta.poster) {
    try {
      const photo = new File([meta.poster], `${base}-poster.jpg`, { type: meta.poster.type });
      poster = await uploadPhoto(orgId, storeId, photo, crypto.randomUUID());
    } catch {
      poster = null; // the video still goes up; the store shows its first frame instead
    }
  }
  if (signal?.aborted) throw new DOMException("Upload cancelled", "AbortError");
  const tickets = await catalogApi.createUpload(
    orgId,
    {
      purpose: "product_video",
      filename: file.name.slice(0, 200) || "video",
      content_type: type,
      size: file.size,
      sha256: await sha256Hex(file),
      store_id: storeId,
      renditions: [],
    },
    crypto.randomUUID(),
  );
  const ticket = tickets.uploads.find((u) => u.variant === "master");
  if (!ticket) throw new ApiError(500, "internal", undefined, undefined, undefined, undefined);
  onProgress("uploading", 0);
  await putWithProgress(
    ticket.url,
    file,
    type,
    (f) => onProgress("uploading", Math.round(f * 100)),
    signal,
  );
  onProgress("finishing", 100);
  const asset = await catalogApi.completeUpload(orgId, tickets.asset_id);
  return { asset, poster, durationMs: meta.durationMs };
}
